// src/pages/manager/OrderDiscountManager.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef, lazy, Suspense } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Alert,
  Snackbar,
  Fade,
  useTheme,
  alpha,
  Pagination,
  CircularProgress,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  InputLabel,
  Tooltip,
  Paper,
  useMediaQuery,
  Stack,
  Divider,
  LinearProgress,
  TablePagination,
  Fab,
} from '@mui/material';

import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Discount as DiscountIcon,
  CardGiftcard as GiftIcon,
  Percent as PercentIcon,
  AttachMoney as MoneyIcon,
  Delete as DeleteIcon,
  History as HistoryIcon,
  Cancel as CancelIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Done as DoneIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  TableRestaurant as TableRestaurantIcon,
  Restaurant as RestaurantIcon,
  Receipt as ReceiptIcon,
  CheckCircle as CheckCircleIcon,
  Autorenew as AutorenewIcon,
  Pause as PauseIcon,
  PlayArrow as PlayArrowIcon,
} from '@mui/icons-material';

// Lazy load components for better performance
const Preloader = lazy(() => import('../../../theme/Preloader'));
const Header = lazy(() => import('../../../components/RestaurantPOS/manager/Header'));
const Footer = lazy(() => import('../../../components/RestaurantPOS/manager/Footer'));

// Lazy load sidebars
const AfricanSidebar = lazy(() => import('../../../components/RestaurantPOS/manager/african/Sidebar'));
const ContinentalSidebar = lazy(() => import('../../../components/RestaurantPOS/manager/continental/Sidebar'));
const LoungeSidebar = lazy(() => import('../../../components/RestaurantPOS/manager/lounge/Sidebar'));

// ============================================
// CONSTANTS & CONFIGURATION
// ============================================

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
const API_PREFIX = process.env.REACT_APP_API_PREFIX || '/pos_v1';
const FULL_API_URL = `${API_BASE_URL}${API_PREFIX}`;

const ITEMS_PER_PAGE = 8;

// Order Status Constants
const ORDER_STATUS = {
  PENDING: 'PENDING',
  IN_PROGRESS: 'IN_PROGRESS',
  READY: 'READY',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED'
};

const STATUS_CONFIG = {
  [ORDER_STATUS.PENDING]: { label: 'Pending', color: '#FF9800', bgColor: '#fff3e0' },
  [ORDER_STATUS.IN_PROGRESS]: { label: 'In Progress', color: '#2196F3', bgColor: '#e3f2fd' },
  [ORDER_STATUS.READY]: { label: 'Ready', color: '#4CAF50', bgColor: '#e8f5e9' },
  [ORDER_STATUS.COMPLETED]: { label: 'Completed', color: '#9E9E9E', bgColor: '#f5f5f5' },
  [ORDER_STATUS.CANCELLED]: { label: 'Cancelled', color: '#F44336', bgColor: '#ffebee' }
};

// Location configurations - matches the pattern from LocationTables
const LOCATION_CONFIG = {
  african: {
    id: 5,
    name: 'African',
    color: '#2E7D32',
    lightColor: '#4CAF50',
    icon: '🌍',
    sidebar: AfricanSidebar,
    path: '/african-discounts'
  },
  continental: {
    id: 3,
    name: 'Continental',
    color: '#1565C0',
    lightColor: '#1976D2',
    icon: '🌐',
    sidebar: ContinentalSidebar,
    path: '/continental-discounts'
  },
  lounge: {
    id: 4,
    name: 'Lounge',
    color: '#6A1B9A',
    lightColor: '#9C27B0',
    icon: '🍸',
    sidebar: LoungeSidebar,
    path: '/lounge-discounts'
  }
};

// Auto-refresh intervals (in milliseconds)
const REFRESH_INTERVALS = {
  FAST: 15000,
  NORMAL: 30000,
  SLOW: 60000,
  VERY_SLOW: 120000
};

const REFRESH_INTERVAL_LABELS = {
  [REFRESH_INTERVALS.FAST]: '15s',
  [REFRESH_INTERVALS.NORMAL]: '30s',
  [REFRESH_INTERVALS.SLOW]: '1m',
  [REFRESH_INTERVALS.VERY_SLOW]: '2m'
};

// ============================================
// UTILITY FUNCTIONS
// ============================================

const formatCurrency = (amount, currency = '₦') => {
  if (!amount && amount !== 0) return `${currency} 0.00`;
  return `${currency} ${Number(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatTime = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return 'N/A';
  }
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return 'N/A';
  }
};

const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return `${formatDate(dateString)} at ${formatTime(dateString)}`;
  } catch {
    return 'N/A';
  }
};

const getStatusColor = (status) => {
  const config = STATUS_CONFIG[status?.toUpperCase()];
  return config?.color || '#9E9E9E';
};

const getStatusLabel = (status) => {
  const config = STATUS_CONFIG[status?.toUpperCase()];
  return config?.label || status || 'Unknown';
};

const getDiscountTypeLabel = (type) => {
  switch (type?.toUpperCase()) {
    case 'PERCENTAGE': return 'Percentage';
    case 'FIXED': return 'Fixed Amount';
    case 'COMPLIMENTARY': return 'Complimentary';
    default: return type || 'None';
  }
};

const getDiscountDisplayText = (order) => {
  if (order.is_complimentary) {
    return 'Complimentary';
  }
  if (order.discount > 0) {
    if (order.discount_type === 'PERCENTAGE') {
      const originalTotal = order.original_total_amount || (order.subtotal + (order.tax || 0));
      if (originalTotal > 0) {
        const percentage = ((order.discount / originalTotal) * 100);
        return `${percentage.toFixed(1)}%`;
      }
      return `${order.discount}%`;
    } else if (order.discount_type === 'FIXED') {
      return formatCurrency(order.discount);
    }
    return formatCurrency(order.discount);
  }
  return 'No Discount';
};

const getActionTypeColor = (actionType) => {
  if (actionType === 'COMPLIMENTARY_APPLIED') return '#9C27B0';
  if (actionType === 'DISCOUNT_APPLIED') return '#FF6F00';
  if (actionType === 'DISCOUNT_REMOVED') return '#F44336';
  if (actionType === 'COMPLIMENTARY_REMOVED') return '#F44336';
  return '#1976D2';
};

const getActionTypeIcon = (actionType) => {
  if (actionType === 'COMPLIMENTARY_APPLIED') return <GiftIcon fontSize="small" />;
  if (actionType === 'DISCOUNT_APPLIED') return <DiscountIcon fontSize="small" />;
  if (actionType === 'DISCOUNT_REMOVED') return <CancelIcon fontSize="small" />;
  if (actionType === 'COMPLIMENTARY_REMOVED') return <CancelIcon fontSize="small" />;
  return <HistoryIcon fontSize="small" />;
};

const getActionTypeLabel = (actionType) => {
  if (!actionType) return 'Unknown Action';
  return actionType.replace(/_/g, ' ');
};

// ============================================
// TOKEN & AUTH FUNCTIONS
// ============================================

const isTokenExpired = (token) => {
  try {
    if (!token) return true;
    const payload = token.split('.')[1];
    if (!payload) return true;
    const decoded = JSON.parse(atob(payload));
    if (!decoded.exp) return true;
    return decoded.exp * 1000 < Date.now();
  } catch (err) {
    console.error('[DiscountManager] Error checking token expiry:', err);
    return true;
  }
};

const getTokenExpiryTime = (token) => {
  try {
    if (!token) return 0;
    const payload = token.split('.')[1];
    if (!payload) return 0;
    const decoded = JSON.parse(atob(payload));
    return decoded.exp ? decoded.exp * 1000 : 0;
  } catch (err) {
    return 0;
  }
};

const refreshAuthToken = async () => {
  try {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      console.warn('[DiscountManager] No refresh token available');
      throw new Error('No refresh token available');
    }

    console.debug('[DiscountManager] Refreshing auth token...');
    const response = await fetch(`${FULL_API_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!response.ok) {
      throw new Error(`Refresh failed with status: ${response.status}`);
    }

    const data = await response.json();
    if (data.access_token) {
      localStorage.setItem('access_token', data.access_token);
      console.debug('[DiscountManager] Token refreshed successfully');
      return data.access_token;
    }
    throw new Error('No access token in refresh response');
  } catch (err) {
    console.error('[DiscountManager] Token refresh failed:', err.message);
    localStorage.clear();
    window.location.href = '/restaurantAuth';
    return null;
  }
};

// ============================================
// API REQUEST FUNCTION
// ============================================

const apiRequest = async (endpoint, options = {}) => {
  let token = localStorage.getItem('access_token');
  
  if (!token || isTokenExpired(token)) {
    console.debug('[DiscountManager] Token invalid, attempting refresh...');
    token = await refreshAuthToken();
    if (!token) {
      throw new Error('Authentication required');
    }
  }

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
    'Authorization': `Bearer ${token}`,
  };

  const url = `${FULL_API_URL}${endpoint}`;
  console.debug(`[DiscountManager] API Request: ${options.method || 'GET'} ${url}`);

  try {
    const response = await fetch(url, { ...options, headers });

    if (response.status === 401) {
      console.debug('[DiscountManager] Received 401, attempting token refresh...');
      const newToken = await refreshAuthToken();
      if (newToken) {
        headers['Authorization'] = `Bearer ${newToken}`;
        const retryResponse = await fetch(url, { ...options, headers });
        if (retryResponse.ok) {
          const contentType = retryResponse.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            return await retryResponse.json();
          }
          return { message: await retryResponse.text() };
        }
        if (retryResponse.status === 401) {
          throw new Error('Authentication failed after refresh');
        }
      }
      throw new Error('Authentication failed');
    }

    const contentType = response.headers.get('content-type');
    let data;
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      const text = await response.text();
      data = { message: text || 'Non-JSON response received' };
    }

    if (!response.ok) {
      const errorMessage = data.detail || data.message || `HTTP ${response.status}`;
      throw new Error(errorMessage);
    }

    return data;
  } catch (err) {
    console.error('[DiscountManager] API Error:', err.message);
    throw err;
  }
};

// ============================================
// SUB-COMPONENTS - Memoized for performance
// ============================================

// Stats Card Component - Memoized
const StatsCards = React.memo(({ stats, locationColor }) => (
  <Grid container spacing={2} sx={{ mb: 3 }}>
    <Grid item xs={6} sm={3}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          textAlign: 'center',
          borderRadius: '12px',
          border: `1px solid ${alpha('#000', 0.06)}`,
          bgcolor: '#f8f9fa',
          transition: 'all 0.2s ease',
          '&:hover': { transform: 'translateY(-2px)', boxShadow: 2 },
        }}
      >
        <Typography variant="h4" fontWeight={800} color={locationColor || '#2E7D32'}>
          {stats.total || 0}
        </Typography>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          Total Orders
        </Typography>
      </Paper>
    </Grid>
    <Grid item xs={6} sm={3}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          textAlign: 'center',
          borderRadius: '12px',
          border: `1px solid ${alpha('#FF6F00', 0.15)}`,
          bgcolor: alpha('#FF6F00', 0.04),
          transition: 'all 0.2s ease',
          '&:hover': { transform: 'translateY(-2px)', boxShadow: 2 },
        }}
      >
        <Typography variant="h4" fontWeight={800} color="#FF6F00">
          {stats.discounted || 0}
        </Typography>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          Discounted
        </Typography>
      </Paper>
    </Grid>
    <Grid item xs={6} sm={3}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          textAlign: 'center',
          borderRadius: '12px',
          border: `1px solid ${alpha('#9C27B0', 0.15)}`,
          bgcolor: alpha('#9C27B0', 0.04),
          transition: 'all 0.2s ease',
          '&:hover': { transform: 'translateY(-2px)', boxShadow: 2 },
        }}
      >
        <Typography variant="h4" fontWeight={800} color="#9C27B0">
          {stats.complimentary || 0}
        </Typography>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          Complimentary
        </Typography>
      </Paper>
    </Grid>
    <Grid item xs={6} sm={3}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          textAlign: 'center',
          borderRadius: '12px',
          border: `1px solid ${alpha('#1976D2', 0.15)}`,
          bgcolor: alpha('#1976D2', 0.04),
          transition: 'all 0.2s ease',
          '&:hover': { transform: 'translateY(-2px)', boxShadow: 2 },
        }}
      >
        <Typography variant="h4" fontWeight={800} color="#1976D2">
          {stats.pending || 0}
        </Typography>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          Pending
        </Typography>
      </Paper>
    </Grid>
  </Grid>
));

// Order Card Component - Memoized
const OrderCard = React.memo(({ 
  order, 
  onApplyDiscount, 
  onRemoveDiscount, 
  onViewAudit, 
  onCancelOrder,
  isEditable,
  locationColor 
}) => {
  const theme = useTheme();
  const hasDiscount = parseFloat(order.discount) > 0 || order.is_complimentary;
  const discountColor = order.is_complimentary ? '#9C27B0' : (parseFloat(order.discount) > 0 ? '#FF6F00' : 'transparent');
  
  // Parse items with error handling
  let items = [];
  try {
    items = typeof order.items === 'string' ? JSON.parse(order.items) : (order.items || []);
  } catch {
    items = [];
  }

  const statusColor = getStatusColor(order.order_status);
  const statusLabel = getStatusLabel(order.order_status);

  // Handle card click
  const handleCardClick = () => {
    if (isEditable) {
      onApplyDiscount(order);
    }
  };

  // Stop propagation helpers
  const handleRemoveClick = (e) => {
    e.stopPropagation();
    onRemoveDiscount(order);
  };

  const handleAuditClick = (e) => {
    e.stopPropagation();
    onViewAudit(order);
  };

  const handleCancelClick = (e) => {
    e.stopPropagation();
    onCancelOrder(order);
  };

  return (
    <Card
      onClick={handleCardClick}
      sx={{
        borderRadius: '16px',
        border: hasDiscount ? `2px solid ${discountColor}` : `1px solid ${alpha('#000', 0.08)}`,
        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'visible',
        height: '100%',
        minHeight: 280,
        display: 'flex',
        flexDirection: 'column',
        opacity: isEditable ? 1 : 0.7,
        cursor: isEditable ? 'pointer' : 'default',
        '&:hover': {
          transform: isEditable ? 'translateY(-4px)' : 'none',
          boxShadow: isEditable ? theme.shadows[8] : theme.shadows[1],
          borderColor: isEditable ? discountColor || locationColor || '#2E7D32' : undefined,
        },
      }}
    >
      {/* Discount/Complimentary Badge */}
      {hasDiscount && (
        <Box
          sx={{
            position: 'absolute',
            top: -8,
            right: -8,
            bgcolor: discountColor,
            color: 'white',
            borderRadius: '50%',
            width: 36,
            height: 36,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 3px 12px ${alpha(discountColor, 0.4)}`,
            zIndex: 2,
          }}
        >
          {order.is_complimentary ? <GiftIcon sx={{ fontSize: 18 }} /> : <DiscountIcon sx={{ fontSize: 18 }} />}
        </Box>
      )}

      <CardContent sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Typography variant="h6" fontWeight={800} sx={{ fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>
            {order.order_number}
          </Typography>
          <Chip
            label={statusLabel}
            size="small"
            sx={{
              bgcolor: alpha(statusColor, 0.12),
              color: statusColor,
              fontWeight: 700,
              fontSize: '0.7rem',
              height: 26,
              '& .MuiChip-label': { fontWeight: 700, px: 1.5 },
            }}
          />
        </Box>

        {/* Metadata */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
          {order.table_name && (
            <Chip
              icon={<TableRestaurantIcon sx={{ fontSize: 14 }} />}
              label={order.table_name}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.65rem', fontWeight: 600, height: 22 }}
            />
          )}
          {order.order_type === 'TAKEAWAY' && (
            <Chip
              label="Takeaway"
              size="small"
              sx={{
                bgcolor: alpha('#1976D2', 0.1),
                color: '#1976D2',
                fontWeight: 700,
                fontSize: '0.6rem',
                height: 22,
              }}
            />
          )}
          {order.order_type === 'DINE_IN' && (
            <Chip
              label="Dine In"
              size="small"
              sx={{
                bgcolor: alpha('#4CAF50', 0.1),
                color: '#4CAF50',
                fontWeight: 700,
                fontSize: '0.6rem',
                height: 22,
              }}
            />
          )}
        </Box>

        {/* Waiter & Customer */}
        {order.waiter_name && (
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Waiter: {order.waiter_name}
          </Typography>
        )}
        {order.customer_name && (
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Customer: {order.customer_name}
          </Typography>
        )}

        {/* Items */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
          {items.slice(0, 5).map((item, idx) => (
            <Chip
              key={idx}
              label={`${item.name} ×${item.quantity || 1}`}
              size="small"
              sx={{
                fontSize: '0.7rem',
                fontWeight: 700,
                bgcolor: alpha('#000', 0.04),
                borderRadius: '6px',
                height: 26,
              }}
            />
          ))}
          {items.length > 5 && (
            <Chip
              label={`+${items.length - 5} more`}
              size="small"
              sx={{ fontSize: '0.65rem', fontWeight: 600, height: 24 }}
            />
          )}
        </Box>

        {/* Footer with Action Icons */}
        <Box
          sx={{
            mt: 'auto',
            pt: 1.5,
            borderTop: `1px solid ${alpha('#000', 0.06)}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Box>
            {hasDiscount ? (
              <Chip
                icon={order.is_complimentary ? <GiftIcon fontSize="small" /> : <DiscountIcon fontSize="small" />}
                label={getDiscountDisplayText(order)}
                size="small"
                sx={{
                  bgcolor: alpha(discountColor, 0.12),
                  color: discountColor,
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  height: 26,
                }}
              />
            ) : (
              <Chip
                label="No Discount"
                size="small"
                sx={{
                  bgcolor: alpha('#9E9E9E', 0.08),
                  color: '#9E9E9E',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  height: 24,
                }}
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {order.original_total_amount > 0 && parseFloat(order.discount) > 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textDecoration: 'line-through', fontSize: '0.7rem', fontWeight: 600 }}
              >
                {formatCurrency(order.original_total_amount)}
              </Typography>
            )}
            <Typography
              variant="h6"
              fontWeight={800}
              color={order.is_complimentary ? '#9C27B0' : (parseFloat(order.discount) > 0 ? '#FF6F00' : '#2E7D32')}
              sx={{ fontSize: { xs: '0.95rem', sm: '1.1rem' } }}
            >
              {order.is_complimentary ? 'FREE' : formatCurrency(order.total_amount)}
            </Typography>
          </Box>
        </Box>

        {/* Action Icons Row */}
        <Box
          sx={{
            mt: 1.5,
            pt: 1,
            borderTop: `1px solid ${alpha('#000', 0.06)}`,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          {isEditable && (
            <Tooltip title={`Click to ${order.is_complimentary ? 'edit complimentary' : (parseFloat(order.discount) > 0 ? 'edit discount' : 'apply discount')}`}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Chip
                  label={order.is_complimentary ? 'Click to Edit' : (parseFloat(order.discount) > 0 ? 'Click to Edit' : 'Click to Apply')}
                  size="small"
                  icon={order.is_complimentary ? <GiftIcon fontSize="small" /> : (parseFloat(order.discount) > 0 ? <EditIcon fontSize="small" /> : <DiscountIcon fontSize="small" />)}
                  sx={{
                    bgcolor: alpha(locationColor || '#2E7D32', 0.08),
                    color: locationColor || '#2E7D32',
                    fontWeight: 600,
                    fontSize: '0.65rem',
                    height: 24,
                    '&:hover': {
                      bgcolor: alpha(locationColor || '#2E7D32', 0.15),
                    },
                  }}
                />
              </Box>
            </Tooltip>
          )}

          {hasDiscount && isEditable && (
            <Tooltip title="Remove Discount">
              <IconButton
                size="small"
                onClick={handleRemoveClick}
                sx={{
                  bgcolor: alpha('#F44336', 0.08),
                  color: '#F44336',
                  '&:hover': { bgcolor: alpha('#F44336', 0.15) },
                  width: 32,
                  height: 32,
                }}
              >
                <CancelIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title="View Audit">
            <IconButton
              size="small"
              onClick={handleAuditClick}
              sx={{
                bgcolor: alpha('#1976D2', 0.08),
                color: '#1976D2',
                '&:hover': { bgcolor: alpha('#1976D2', 0.15) },
                width: 32,
                height: 32,
              }}
            >
              <HistoryIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {isEditable && order.order_status !== ORDER_STATUS.CANCELLED && (
            <Tooltip title="Cancel Order">
              <IconButton
                size="small"
                onClick={handleCancelClick}
                sx={{
                  bgcolor: alpha('#F44336', 0.08),
                  color: '#F44336',
                  '&:hover': { bgcolor: alpha('#F44336', 0.15) },
                  width: 32,
                  height: 32,
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </CardContent>
    </Card>
  );
});

// Audit Trail Component - Memoized
const AuditTrail = React.memo(({ audits, theme }) => {
  if (!audits || audits.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <HistoryIcon sx={{ fontSize: 48, color: 'text.disabled', opacity: 0.3 }} />
        <Typography variant="body1" color="text.secondary" sx={{ mt: 2, fontWeight: 600 }}>
          No audit history available
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', pl: 3 }}>
      <Box
        sx={{
          position: 'absolute',
          left: 12,
          top: 0,
          bottom: 0,
          width: 2,
          bgcolor: alpha('#000', 0.08),
        }}
      />

      {audits.map((audit, index) => {
        const color = getActionTypeColor(audit.action_type);
        const icon = getActionTypeIcon(audit.action_type);
        const label = getActionTypeLabel(audit.action_type);

        return (
          <Box
            key={audit.id || audit.created_at || index}
            sx={{ position: 'relative', mb: 3, '&:last-child': { mb: 0 } }}
          >
            <Box
              sx={{
                position: 'absolute',
                left: -24,
                top: 0,
                width: 24,
                height: 24,
                borderRadius: '50%',
                bgcolor: color,
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1,
                boxShadow: `0 2px 8px ${alpha(color, 0.3)}`,
              }}
            >
              {icon}
            </Box>

            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                bgcolor: alpha(color, 0.04),
                border: `1px solid ${alpha(color, 0.15)}`,
                borderRadius: '12px',
                ml: 2,
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: alpha(color, 0.08),
                  boxShadow: theme.shadows[2],
                },
              }}
            >
              <Stack spacing={1}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ color }}>
                    {label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    {formatDateTime(audit.created_at)}
                  </Typography>
                </Box>

                <Divider sx={{ borderColor: alpha(color, 0.1) }} />

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {audit.previous_discount !== undefined && audit.new_discount !== undefined && (
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      <strong>Discount:</strong> {formatCurrency(audit.previous_discount || 0)} → {formatCurrency(audit.new_discount || 0)}
                    </Typography>
                  )}
                  {audit.previous_total !== undefined && audit.new_total !== undefined && (
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      <strong>Total:</strong> {formatCurrency(audit.previous_total || 0)} → {formatCurrency(audit.new_total || 0)}
                    </Typography>
                  )}
                  {audit.previous_discount_type && audit.new_discount_type && (
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      <strong>Type:</strong> {getDiscountTypeLabel(audit.previous_discount_type)} → {getDiscountTypeLabel(audit.new_discount_type)}
                    </Typography>
                  )}
                </Box>

                {audit.reason && (
                  <Box sx={{ mt: 1, p: 1.5, bgcolor: alpha('#000', 0.02), borderRadius: '8px' }}>
                    <Typography variant="body2" fontWeight={600}>
                      <strong>Reason:</strong> {audit.reason}
                    </Typography>
                  </Box>
                )}

                {audit.notes && (
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    <strong>Notes:</strong> {audit.notes}
                  </Typography>
                )}

                {audit.approver_name && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                    <PersonIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      Approved by: {audit.approver_name}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Paper>
          </Box>
        );
      })}
    </Box>
  );
});

// ============================================
// MAIN COMPONENT - OrderDiscountManager
// ============================================

const OrderDiscountManager = ({ locationKey = 'african' }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  // Get location config
  const locationConfig = useMemo(() => 
    LOCATION_CONFIG[locationKey] || LOCATION_CONFIG.african,
    [locationKey]
  );
  
  const { name: locationName, color: locationColor, id: locationId, sidebar: SidebarComponent } = locationConfig;

  // ============================================
  // STATE MANAGEMENT
  // ============================================

  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [stats, setStats] = useState({ total: 0, discounted: 0, complimentary: 0, pending: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activeMenu, setActiveMenu] = useState('Discount Management');

  // Auto-refresh state
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(REFRESH_INTERVALS.NORMAL);
  const [countdown, setCountdown] = useState(30);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [discountFilter, setDiscountFilter] = useState('ALL');

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ITEMS_PER_PAGE);

  // Dialog states
  const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
  const [auditDialogOpen, setAuditDialogOpen] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [auditHistory, setAuditHistory] = useState([]);
  const [isLoadingAudit, setIsLoadingAudit] = useState(false);

  // Discount form states
  const [discountType, setDiscountType] = useState('PERCENTAGE');
  const [discountValue, setDiscountValue] = useState('');
  const [discountReason, setDiscountReason] = useState('');
  const [discountNotes, setDiscountNotes] = useState('');
  const [isSubmittingDiscount, setIsSubmittingDiscount] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  // Remove discount states
  const [removeReason, setRemoveReason] = useState('');
  const [removeNotes, setRemoveNotes] = useState('');
  const [isRemovingDiscount, setIsRemovingDiscount] = useState(false);

  // Cancel order states
  const [cancelReason, setCancelReason] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Refs
  const searchTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);

  // ============================================
  // DIALOG HANDLERS
  // ============================================

  const handleCloseDiscountDialog = useCallback(() => {
    if (isSubmittingDiscount) return;
    setDiscountDialogOpen(false);
    setSelectedOrder(null);
    setDiscountType('PERCENTAGE');
    setDiscountValue('');
    setDiscountReason('');
    setDiscountNotes('');
    setFormErrors({});
  }, [isSubmittingDiscount]);

  const handleCloseRemoveDialog = useCallback(() => {
    if (isRemovingDiscount) return;
    setRemoveDialogOpen(false);
    setSelectedOrder(null);
    setRemoveReason('');
    setRemoveNotes('');
  }, [isRemovingDiscount]);

  const handleCloseAuditDialog = useCallback(() => {
    setAuditDialogOpen(false);
    setSelectedOrder(null);
    setAuditHistory([]);
  }, []);

  const handleCloseCancelDialog = useCallback(() => {
    if (isCancelling) return;
    setCancelDialogOpen(false);
    setSelectedOrder(null);
    setCancelReason('');
  }, [isCancelling]);

  const handleOpenDiscountDialog = useCallback((order) => {
    // Check if order can be edited
    if (order.order_status === ORDER_STATUS.COMPLETED || order.order_status === ORDER_STATUS.CANCELLED) {
      setSnackbar({
        open: true,
        message: `⚠️ Cannot edit ${getStatusLabel(order.order_status)} order`,
        severity: 'warning',
      });
      return;
    }

    setSelectedOrder(order);
    setDiscountType(order.is_complimentary ? 'COMPLIMENTARY' : (order.discount_type || 'PERCENTAGE'));
    setDiscountValue(order.discount_type === 'PERCENTAGE' ? 
      ((order.discount / (order.original_total_amount || order.subtotal + order.tax)) * 100).toFixed(1) : 
      (order.discount || '')
    );
    setDiscountReason(order.discount_reason || '');
    setDiscountNotes(order.discount_approval_notes || '');
    setFormErrors({});
    setDiscountDialogOpen(true);
  }, []);

  const handleOpenRemoveDialog = useCallback((order) => {
    if (order.order_status === ORDER_STATUS.COMPLETED || order.order_status === ORDER_STATUS.CANCELLED) {
      setSnackbar({
        open: true,
        message: `⚠️ Cannot remove discount from ${getStatusLabel(order.order_status)} order`,
        severity: 'warning',
      });
      return;
    }
    setSelectedOrder(order);
    setRemoveReason('');
    setRemoveNotes('');
    setRemoveDialogOpen(true);
  }, []);

  const handleOpenAuditDialog = useCallback(async (order) => {
    setSelectedOrder(order);
    setAuditDialogOpen(true);
    await fetchAuditHistory(order.id);
  }, []);

  const handleOpenCancelDialog = useCallback((order) => {
    if (order.order_status === ORDER_STATUS.COMPLETED || order.order_status === ORDER_STATUS.CANCELLED) {
      setSnackbar({
        open: true,
        message: `⚠️ Cannot cancel ${getStatusLabel(order.order_status)} order`,
        severity: 'warning',
      });
      return;
    }
    setSelectedOrder(order);
    setCancelReason('');
    setCancelDialogOpen(true);
  }, []);

  // ============================================
  // DATA FETCHING
  // ============================================

  const fetchEligibleOrders = useCallback(async () => {
    try {
      console.debug(`[DiscountManager-${locationKey}] Fetching eligible orders...`);
      setIsFetching(true);

      const data = await apiRequest('/orders/discounts/eligible-orders?limit=500');
      console.debug(`[DiscountManager-${locationKey}] Eligible orders fetched:`, data);

      if (isMountedRef.current) {
        const ordersList = Array.isArray(data) ? data : (data.items || []);
        
        // Filter orders by location if locationId is provided
        const filteredByLocation = locationId 
          ? ordersList.filter(order => order.location_id === locationId || order.location === locationKey)
          : ordersList;
          
        setOrders(filteredByLocation);
        updateStats(filteredByLocation);
        setFilteredOrders(filteredByLocation);
        console.debug(`[DiscountManager-${locationKey}] Loaded ${filteredByLocation.length} eligible orders`);
      }
    } catch (err) {
      console.error(`[DiscountManager-${locationKey}] Error fetching eligible orders:`, err);
      if (isMountedRef.current) {
        if (err.message === 'Authentication required' || err.message === 'Authentication failed after refresh') {
          setSnackbar({
            open: true,
            message: 'Session expired. Please login again.',
            severity: 'error',
          });
          setTimeout(() => {
            localStorage.clear();
            window.location.href = '/restaurantAuth';
          }, 2000);
        } else {
          setSnackbar({
            open: true,
            message: `Failed to fetch orders: ${err.message}`,
            severity: 'error',
          });
        }
      }
    } finally {
      if (isMountedRef.current) {
        setIsFetching(false);
        setIsLoading(false);
        setIsRefreshing(false);
      }
    }
  }, [locationKey, locationId]);

  const updateStats = useCallback((ordersList) => {
    const total = ordersList.length;
    const discounted = ordersList.filter(o => parseFloat(o.discount) > 0 && !o.is_complimentary).length;
    const complimentary = ordersList.filter(o => o.is_complimentary).length;
    const pending = ordersList.filter(o => o.order_status === 'PENDING').length;

    setStats({ total, discounted, complimentary, pending });
  }, []);

  const fetchAuditHistory = useCallback(async (orderId) => {
    try {
      console.debug(`[DiscountManager-${locationKey}] Fetching audit history for order ${orderId}...`);
      setIsLoadingAudit(true);

      const data = await apiRequest(`/orders/discounts/${orderId}/audit`);
      console.debug(`[DiscountManager-${locationKey}] Audit history fetched:`, data);

      if (isMountedRef.current) {
        setAuditHistory(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error(`[DiscountManager-${locationKey}] Error fetching audit history:`, err);
      if (isMountedRef.current) {
        setSnackbar({
          open: true,
          message: `Failed to fetch audit history: ${err.message}`,
          severity: 'error',
        });
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoadingAudit(false);
      }
    }
  }, [locationKey]);

  // ============================================
  // CANCEL ORDER
  // ============================================

  const handleCancelOrder = useCallback(async () => {
    if (!selectedOrder) return;

    setIsCancelling(true);
    try {
      console.debug(`[DiscountManager-${locationKey}] Cancelling order ${selectedOrder.id}...`);

      const response = await apiRequest(`/orders/${selectedOrder.id}`, {
        method: 'DELETE',
        body: JSON.stringify({ reason: cancelReason.trim() || null }),
      });

      console.debug(`[DiscountManager-${locationKey}] Order cancelled:`, response);

      await fetchEligibleOrders();

      setSnackbar({
        open: true,
        message: `Order ${selectedOrder.order_number} cancelled successfully`,
        severity: 'success',
      });

      handleCloseCancelDialog();
    } catch (err) {
      console.error(`[DiscountManager-${locationKey}] Error cancelling order:`, err);
      setSnackbar({
        open: true,
        message: `Failed to cancel order: ${err.message}`,
        severity: 'error',
      });
    } finally {
      setIsCancelling(false);
    }
  }, [selectedOrder, cancelReason, fetchEligibleOrders, handleCloseCancelDialog, locationKey]);

  // ============================================
  // FILTERING & SEARCH
  // ============================================

  const applyFilters = useCallback(() => {
    let filtered = [...orders];

    // Status filter
    if (statusFilter !== 'ALL') {
      filtered = filtered.filter(order =>
        order.order_status?.toUpperCase() === statusFilter
      );
    }

    // Discount filter
    if (discountFilter === 'DISCOUNTED') {
      filtered = filtered.filter(order => {
        const discountAmount = parseFloat(order.discount) || 0;
        return discountAmount > 0 || order.is_complimentary === true;
      });
    } else if (discountFilter === 'COMPLIMENTARY') {
      filtered = filtered.filter(order => order.is_complimentary === true);
    } else if (discountFilter === 'NO_DISCOUNT') {
      filtered = filtered.filter(order => {
        const discountAmount = parseFloat(order.discount) || 0;
        return discountAmount === 0 && !order.is_complimentary;
      });
    }

    // Search filter
    if (searchTerm && searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(order => {
        const orderNumber = (order.order_number || '').toLowerCase();
        const customerName = (order.customer_name || '').toLowerCase();
        const tableName = (order.table_name || '').toLowerCase();
        const waiterName = (order.waiter_name || '').toLowerCase();
        const orderStatus = (order.order_status || '').toLowerCase();
        const orderType = (order.order_type || '').toLowerCase();

        return orderNumber.includes(term) ||
          customerName.includes(term) ||
          tableName.includes(term) ||
          waiterName.includes(term) ||
          orderStatus.includes(term) ||
          orderType.includes(term);
      });
    }

    // Sort by newest first
    filtered.sort((a, b) => {
      const dateA = new Date(a.order_time || a.created_date || 0);
      const dateB = new Date(b.order_time || b.created_date || 0);
      return dateB - dateA;
    });

    setFilteredOrders(filtered);
    setPage(0);
  }, [orders, statusFilter, discountFilter, searchTerm]);

  // ============================================
  // DISCOUNT APPLICATION
  // ============================================

  const validateDiscountForm = useCallback(() => {
    const errors = {};
    let isValid = true;

    // For complimentary, we don't need a discount value
    if (discountType !== 'COMPLIMENTARY') {
      const value = parseFloat(discountValue);
      if (!discountValue || isNaN(value) || value <= 0) {
        errors.discountValue = 'Please enter a valid discount value';
        isValid = false;
      }

      if (discountType === 'PERCENTAGE' && value > 100) {
        errors.discountValue = 'Percentage discount cannot exceed 100%';
        isValid = false;
      }

      if (discountType === 'FIXED' && selectedOrder) {
        const maxDiscount = selectedOrder.subtotal + (selectedOrder.tax || 0);
        if (value > maxDiscount) {
          errors.discountValue = `Discount cannot exceed order total (${formatCurrency(maxDiscount)})`;
          isValid = false;
        }
      }
    }

    // Reason is always required
    if (!discountReason || discountReason.trim().length < 3) {
      errors.discountReason = 'Please provide a reason (minimum 3 characters)';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  }, [discountType, discountValue, discountReason, selectedOrder]);

  const handleApplyDiscount = useCallback(async () => {
    if (!validateDiscountForm()) return;

    setIsSubmittingDiscount(true);

    try {
      let payload;
      let successMessage;

      if (discountType === 'COMPLIMENTARY') {
        payload = {
          discount_type: 'COMPLIMENTARY',
          discount_value: 0,
          reason: discountReason.trim(),
          notes: discountNotes.trim() || null,
        };
        successMessage = `🎉 Order ${selectedOrder.order_number} marked as complimentary successfully!`;
      } else {
        const value = parseFloat(discountValue);
        payload = {
          discount_type: discountType,
          discount_value: value,
          reason: discountReason.trim(),
          notes: discountNotes.trim() || null,
        };
        successMessage = `✅ ${discountType} discount of ${discountType === 'PERCENTAGE' ? `${value}%` : formatCurrency(value)} applied to order ${selectedOrder.order_number}`;
      }

      console.debug(`[DiscountManager-${locationKey}] Applying discount:`, { orderId: selectedOrder.id, payload });

      const response = await apiRequest(`/orders/discounts/${selectedOrder.id}/apply`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      console.debug(`[DiscountManager-${locationKey}] Discount applied:`, response);

      await new Promise(resolve => setTimeout(resolve, 500));
      await fetchEligibleOrders();

      setSnackbar({
        open: true,
        message: successMessage,
        severity: 'success',
      });

      handleCloseDiscountDialog();
    } catch (err) {
      console.error(`[DiscountManager-${locationKey}] Error applying discount:`, err);
      setSnackbar({
        open: true,
        message: `❌ Failed to apply discount: ${err.message}`,
        severity: 'error',
      });
    } finally {
      setIsSubmittingDiscount(false);
    }
  }, [discountType, discountValue, discountReason, discountNotes, selectedOrder, validateDiscountForm, handleCloseDiscountDialog, fetchEligibleOrders, locationKey]);

  const handleRemoveDiscount = useCallback(async () => {
    setIsRemovingDiscount(true);

    try {
      const payload = {
        reason: removeReason.trim() || 'Discount removed by manager',
        notes: removeNotes.trim() || null,
      };

      console.debug(`[DiscountManager-${locationKey}] Removing discount:`, { orderId: selectedOrder.id, payload });

      const response = await apiRequest(`/orders/discounts/${selectedOrder.id}/remove`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      console.debug(`[DiscountManager-${locationKey}] Discount removed:`, response);

      await new Promise(resolve => setTimeout(resolve, 500));
      await fetchEligibleOrders();

      setSnackbar({
        open: true,
        message: `✅ Discount removed from order ${selectedOrder.order_number}.`,
        severity: 'success',
      });

      handleCloseRemoveDialog();
    } catch (err) {
      console.error(`[DiscountManager-${locationKey}] Error removing discount:`, err);
      setSnackbar({
        open: true,
        message: `❌ Failed to remove discount: ${err.message}`,
        severity: 'error',
      });
    } finally {
      setIsRemovingDiscount(false);
    }
  }, [removeReason, removeNotes, selectedOrder, handleCloseRemoveDialog, fetchEligibleOrders, locationKey]);

  // ============================================
  // REFRESH HANDLER
  // ============================================

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await fetchEligibleOrders();
    setSnackbar({
      open: true,
      message: 'Orders refreshed successfully',
      severity: 'success',
    });
  }, [fetchEligibleOrders]);

  // ============================================
  // RENDER HELPERS
  // ============================================

  const isOrderEditable = useCallback((order) => {
    return order.order_status !== ORDER_STATUS.COMPLETED && order.order_status !== ORDER_STATUS.CANCELLED;
  }, []);

  // ============================================
  // EFFECTS
  // ============================================

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(applyFilters, 300);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [applyFilters]);

  // Auto-refresh effect
  useEffect(() => {
    let refreshTimer = null;
    let countdownTimer = null;

    if (autoRefreshEnabled && !isLoading && !isFetching) {
      setCountdown(Math.floor(refreshInterval / 1000));

      countdownTimer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            return Math.floor(refreshInterval / 1000);
          }
          return prev - 1;
        });
      }, 1000);

      refreshTimer = setInterval(() => {
        console.debug(`[DiscountManager-${locationKey}] Auto-refreshing orders...`);
        fetchEligibleOrders();
      }, refreshInterval);
    }

    return () => {
      if (refreshTimer) clearInterval(refreshTimer);
      if (countdownTimer) clearInterval(countdownTimer);
    };
  }, [autoRefreshEnabled, refreshInterval, isLoading, isFetching, fetchEligibleOrders, locationKey]);

  // Session management - auto-refresh token
  useEffect(() => {
    const checkAndRefreshToken = async () => {
      const token = localStorage.getItem('access_token');
      if (!token) return;

      const expiryTime = getTokenExpiryTime(token);
      const timeLeft = expiryTime - Date.now();

      if (timeLeft > 0 && timeLeft < 120000) {
        console.log(`[DiscountManager-${locationKey}] Auto-refreshing token...`);
        await refreshAuthToken();
      }
    };

    const refreshInterval = setInterval(checkAndRefreshToken, 60000);
    return () => clearInterval(refreshInterval);
  }, [locationKey]);

  // Initialization
  useEffect(() => {
    isMountedRef.current = true;
    console.debug(`[DiscountManager-${locationKey}] Initializing...`);

    fetchEligibleOrders();

    const timer = setTimeout(() => {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }, 1500);

    return () => {
      clearTimeout(timer);
      isMountedRef.current = false;
    };
  }, [fetchEligibleOrders, locationKey]);

  // ============================================
  // LOADING STATE
  // ============================================

  if (isLoading) {
    return (
      <Suspense fallback={<div>Loading...</div>}>
        <Preloader text={`Loading ${locationName} Discount Management...`} size={70} fullScreen={true} />
      </Suspense>
    );
  }

  // ============================================
  // MAIN RENDER
  // ============================================

  const paginatedOrders = filteredOrders.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <div className="epos-wrapper">
        <div className="epos-main-layout">
          <SidebarComponent activeMenu={activeMenu} setActiveMenu={setActiveMenu} />

          <div className="epos-main">
            <Header />
            
            <div className="epos-content">
              {/* Header Card with Location Info */}
              <div className="hero-card">
                <div>
                  <div className="hero-title" style={{ color: locationColor, fontWeight: 'bold' }}>
                    {locationConfig.icon} {locationName} Discount Management
                  </div>
                  <div className="hero-text-wrapper">
                    <div className="hero-tagline">
                      <span className="hero-tagline-icon">✦</span>
                      Manage discounts and complimentary orders
                    </div>
                  </div>
                </div>
                <button 
                  className="new-order-btn" 
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  style={{ backgroundColor: locationColor }}
                >
                  {isRefreshing ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon style={{ marginRight: '8px' }} />}
                  {isRefreshing ? 'Refreshing...' : 'Refresh Orders'}
                </button>
              </div>

              {/* Stats Cards */}
              <StatsCards stats={stats} locationColor={locationColor} />

              {/* Loading Indicator */}
              {isFetching && (
                <div style={{ width: '100%', marginBottom: '16px' }}>
                  <LinearProgress color="primary" />
                </div>
              )}

              {/* Filters - Using div with inline styles to match LocationTables pattern */}
              <div style={{ 
                padding: '12px 16px',
                marginBottom: '20px',
                borderRadius: '12px',
                border: '1px solid rgba(0,0,0,0.06)',
                display: 'flex',
                flexDirection: window.innerWidth < 600 ? 'column' : 'row',
                gap: '16px',
                flexWrap: 'wrap',
                backgroundColor: 'white'
              }}>
                <TextField
                  size="small"
                  placeholder="Search by order number, customer, table, or waiter..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{
                    flex: 1,
                    minWidth: '200px',
                    maxWidth: { xs: '100%', sm: '350px' },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '10px',
                      bgcolor: '#fafafa',
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#94a3b8' }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearchTerm('')}>
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: '140px' } }}>
                  <InputLabel sx={{ fontWeight: 600 }}>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    label="Status"
                    sx={{ borderRadius: '10px', bgcolor: '#fafafa' }}
                  >
                    <MenuItem value="ALL">All Status</MenuItem>
                    <MenuItem value="PENDING">Pending</MenuItem>
                    <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
                    <MenuItem value="READY">Ready</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: '160px' } }}>
                  <InputLabel sx={{ fontWeight: 600 }}>Discount Status</InputLabel>
                  <Select
                    value={discountFilter}
                    onChange={(e) => setDiscountFilter(e.target.value)}
                    label="Discount Status"
                    sx={{ borderRadius: '10px', bgcolor: '#fafafa' }}
                  >
                    <MenuItem value="ALL">All Orders</MenuItem>
                    <MenuItem value="DISCOUNTED">Discounted</MenuItem>
                    <MenuItem value="COMPLIMENTARY">Complimentary</MenuItem>
                    <MenuItem value="NO_DISCOUNT">No Discount</MenuItem>
                  </Select>
                </FormControl>

                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  marginLeft: window.innerWidth >= 600 ? 'auto' : 0
                }}>
                  <Tooltip title={autoRefreshEnabled ? `Auto-refresh every ${Math.floor(refreshInterval/1000)}s` : 'Auto-refresh paused'}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '4px',
                      padding: '4px 12px',
                      borderRadius: '20px',
                      backgroundColor: autoRefreshEnabled ? `rgba(46, 125, 50, 0.08)` : 'rgba(158, 158, 158, 0.08)',
                      border: `1px solid ${autoRefreshEnabled ? 'rgba(46, 125, 50, 0.2)' : 'rgba(158, 158, 158, 0.2)'}`,
                    }}>
                      <IconButton
                        size="small"
                        onClick={() => setAutoRefreshEnabled(!autoRefreshEnabled)}
                        sx={{
                          color: autoRefreshEnabled ? locationColor : '#9E9E9E',
                          padding: '4px',
                        }}
                      >
                        {autoRefreshEnabled ? <AutorenewIcon fontSize="small" /> : <PauseIcon fontSize="small" />}
                      </IconButton>
                      {autoRefreshEnabled && (
                        <Typography variant="caption" fontWeight={700} sx={{ color: locationColor, minWidth: '32px' }}>
                          {countdown}s
                        </Typography>
                      )}
                      <FormControl size="small" sx={{ minWidth: '70px' }}>
                        <Select
                          value={refreshInterval}
                          onChange={(e) => setRefreshInterval(Number(e.target.value))}
                          disabled={!autoRefreshEnabled}
                          sx={{
                            height: '28px',
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            bgcolor: 'transparent',
                            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                            '& .MuiSelect-select': { py: 0, px: 0.5 },
                          }}
                        >
                          <MenuItem value={REFRESH_INTERVALS.FAST}>15s</MenuItem>
                          <MenuItem value={REFRESH_INTERVALS.NORMAL}>30s</MenuItem>
                          <MenuItem value={REFRESH_INTERVALS.SLOW}>1m</MenuItem>
                          <MenuItem value={REFRESH_INTERVALS.VERY_SLOW}>2m</MenuItem>
                        </Select>
                      </FormControl>
                    </div>
                  </Tooltip>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, display: { xs: 'none', sm: 'block' } }}
                  >
                    {filteredOrders.length} orders
                  </Typography>
                </div>
              </div>

              {/* Orders Grid */}
              {filteredOrders.length === 0 && !isFetching ? (
                <div className="bottom-grid">
                  <div className="panel" style={{ textAlign: 'center', padding: '40px 20px' }}>
                    <DiscountIcon style={{ fontSize: 60, color: '#94a3b8', marginBottom: '16px' }} />
                    <Typography variant="h6" color="textSecondary" gutterBottom>
                      No Eligible Orders
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      No orders are currently eligible for discount or complimentary treatment in the {locationName} location.
                    </Typography>
                  </div>
                </div>
              ) : (
                <>
                  <div className="bottom-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
                    {paginatedOrders.map((order) => (
                      <OrderCard
                        key={order.id}
                        order={order}
                        onApplyDiscount={handleOpenDiscountDialog}
                        onRemoveDiscount={handleOpenRemoveDialog}
                        onViewAudit={handleOpenAuditDialog}
                        onCancelOrder={handleOpenCancelDialog}
                        isEditable={isOrderEditable(order)}
                        locationColor={locationColor}
                      />
                    ))}
                  </div>
                  
                  {/* Pagination */}
                  {filteredOrders.length > rowsPerPage && (
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '24px' }}>
                      <TablePagination
                        component="div"
                        count={filteredOrders.length}
                        page={page}
                        onPageChange={(_, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => {
                          setRowsPerPage(parseInt(e.target.value, 10));
                          setPage(0);
                        }}
                        rowsPerPageOptions={[6, 12, 24, 48]}
                        labelRowsPerPage="Orders per page:"
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </div>

      {/* ============================================ */}
      {/* APPLY DISCOUNT DIALOG */}
      {/* ============================================ */}
      <Dialog
        open={discountDialogOpen}
        onClose={handleCloseDiscountDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            maxHeight: '90vh',
          }
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: `1px solid ${alpha('#000', 0.06)}`,
            pb: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: '#f8f9fa',
            borderRadius: '20px 20px 0 0',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <DiscountIcon sx={{ color: locationColor, fontSize: 28 }} />
            <Typography variant="h5" component="span" fontWeight={700}>
              {selectedOrder?.is_complimentary || parseFloat(selectedOrder?.discount) > 0 ? 'Edit Discount' : 'Apply Discount'}
            </Typography>
          </Box>
          <IconButton onClick={handleCloseDiscountDialog} disabled={isSubmittingDiscount} sx={{ bgcolor: alpha('#000', 0.04) }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          {selectedOrder && (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 3,
                bgcolor: alpha(locationColor, 0.04),
                borderRadius: '10px',
                border: `1px solid ${alpha(locationColor, 0.1)}`,
              }}
            >
              <Typography variant="subtitle2" fontWeight={700}>
                Order: {selectedOrder.order_number}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 0.5 }}>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  Table: {selectedOrder.table_name || 'Takeaway'}
                </Typography>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  Status: {getStatusLabel(selectedOrder.order_status)}
                </Typography>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  Subtotal: {formatCurrency(selectedOrder.subtotal)}
                </Typography>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  Tax: {formatCurrency(selectedOrder.tax || 0)}
                </Typography>
                <Typography variant="body2" fontWeight={700} color={locationColor}>
                  Total: {formatCurrency(selectedOrder.total_amount)}
                </Typography>
              </Box>
              {selectedOrder.is_complimentary && (
                <Alert severity="info" sx={{ mt: 1, borderRadius: '8px' }}>
                  This order is currently marked as complimentary.
                </Alert>
              )}
              {parseFloat(selectedOrder.discount) > 0 && !selectedOrder.is_complimentary && (
                <Alert severity="info" sx={{ mt: 1, borderRadius: '8px' }}>
                  Current discount: {getDiscountTypeLabel(selectedOrder.discount_type)} -{' '}
                  {selectedOrder.discount_type === 'PERCENTAGE'
                    ? `${getDiscountDisplayText(selectedOrder)}`
                    : formatCurrency(selectedOrder.discount)}
                </Alert>
              )}
            </Paper>
          )}

          <FormControl component="fieldset" sx={{ width: '100%', mb: 3 }}>
            <FormLabel component="legend" sx={{ fontWeight: 700, mb: 1 }}>
              Discount Type
            </FormLabel>
            <RadioGroup
              value={discountType}
              onChange={(e) => {
                const newType = e.target.value;
                setDiscountType(newType);
                setDiscountValue('');
                setFormErrors(prev => ({ ...prev, discountValue: '' }));
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
                <FormControlLabel
                  value="PERCENTAGE"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PercentIcon fontSize="small" />
                      <span style={{ fontWeight: 600 }}>Percentage</span>
                    </Box>
                  }
                  sx={{
                    flex: 1,
                    border: `1px solid ${alpha('#000', 0.08)}`,
                    borderRadius: '8px',
                    p: 1,
                    ...(discountType === 'PERCENTAGE' && {
                      borderColor: locationColor,
                      bgcolor: alpha(locationColor, 0.04),
                    }),
                  }}
                />
                <FormControlLabel
                  value="FIXED"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <MoneyIcon fontSize="small" />
                      <span style={{ fontWeight: 600 }}>Fixed Amount</span>
                    </Box>
                  }
                  sx={{
                    flex: 1,
                    border: `1px solid ${alpha('#000', 0.08)}`,
                    borderRadius: '8px',
                    p: 1,
                    ...(discountType === 'FIXED' && {
                      borderColor: locationColor,
                      bgcolor: alpha(locationColor, 0.04),
                    }),
                  }}
                />
                <FormControlLabel
                  value="COMPLIMENTARY"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <GiftIcon fontSize="small" />
                      <span style={{ fontWeight: 600 }}>Complimentary</span>
                    </Box>
                  }
                  sx={{
                    flex: 1,
                    border: `1px solid ${alpha('#000', 0.08)}`,
                    borderRadius: '8px',
                    p: 1,
                    ...(discountType === 'COMPLIMENTARY' && {
                      borderColor: '#9C27B0',
                      bgcolor: alpha('#9C27B0', 0.04),
                    }),
                  }}
                />
              </Box>
            </RadioGroup>
          </FormControl>

          {discountType !== 'COMPLIMENTARY' ? (
            <TextField
              fullWidth
              label={discountType === 'PERCENTAGE' ? 'Discount Percentage (%)' : 'Discount Amount'}
              type="number"
              value={discountValue}
              onChange={(e) => {
                setDiscountValue(e.target.value);
                setFormErrors(prev => ({ ...prev, discountValue: '' }));
              }}
              error={!!formErrors.discountValue}
              helperText={formErrors.discountValue}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: discountType === 'FIXED' ? (
                  <InputAdornment position="start">₦</InputAdornment>
                ) : null,
                endAdornment: discountType === 'PERCENTAGE' ? (
                  <InputAdornment position="end">%</InputAdornment>
                ) : null,
              }}
              inputProps={{
                min: 0,
                max: discountType === 'PERCENTAGE' ? 100 : undefined,
                step: 0.01,
              }}
            />
          ) : (
            <Box sx={{ mb: 2, p: 2, bgcolor: alpha('#9C27B0', 0.04), borderRadius: '8px', border: `1px solid ${alpha('#9C27B0', 0.2)}` }}>
              <Typography variant="subtitle2" fontWeight={700} color="#9C27B0">
                🎁 Complimentary Order
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                This will mark the order as complimentary. The total will be set to ₦0.00.
              </Typography>
            </Box>
          )}

          <TextField
            fullWidth
            label="Reason for Discount"
            placeholder="Please provide a clear reason for this discount..."
            value={discountReason}
            onChange={(e) => {
              setDiscountReason(e.target.value);
              setFormErrors(prev => ({ ...prev, discountReason: '' }));
            }}
            error={!!formErrors.discountReason}
            helperText={formErrors.discountReason}
            multiline
            rows={2}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Additional Notes (Optional)"
            placeholder="Any additional notes..."
            value={discountNotes}
            onChange={(e) => setDiscountNotes(e.target.value)}
            multiline
            rows={2}
          />
        </DialogContent>

        <DialogActions
          sx={{
            borderTop: `1px solid ${alpha('#000', 0.06)}`,
            pt: 2,
            px: 3,
            pb: 3,
            gap: 1.5,
            bgcolor: '#f8f9fa',
            borderRadius: '0 0 20px 20px',
          }}
        >
          <Button
            onClick={handleCloseDiscountDialog}
            disabled={isSubmittingDiscount}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px', px: 4, py: 1 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleApplyDiscount}
            variant="contained"
            disabled={isSubmittingDiscount}
            sx={{
              bgcolor: discountType === 'COMPLIMENTARY' ? '#9C27B0' : locationColor,
              '&:hover': { bgcolor: discountType === 'COMPLIMENTARY' ? '#7B1FA2' : alpha(locationColor, 0.8) },
              '&:disabled': { bgcolor: 'grey.300' },
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '10px',
              px: 5,
              py: 1.2,
              fontSize: '1rem',
            }}
            startIcon={isSubmittingDiscount ? <CircularProgress size={22} color="inherit" /> : <DoneIcon />}
          >
            {isSubmittingDiscount ? 'Applying...' : discountType === 'COMPLIMENTARY' ? 'Mark Complimentary' : 'Apply Discount'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============================================ */}
      {/* REMOVE DISCOUNT DIALOG */}
      {/* ============================================ */}
      <Dialog
        open={removeDialogOpen}
        onClose={handleCloseRemoveDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            maxHeight: '80vh',
          }
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: `1px solid ${alpha('#000', 0.06)}`,
            pb: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: '#f8f9fa',
            borderRadius: '20px 20px 0 0',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CancelIcon sx={{ color: '#F44336', fontSize: 28 }} />
            <Typography variant="h5" component="span" fontWeight={700}>
              Remove Discount
            </Typography>
          </Box>
          <IconButton onClick={handleCloseRemoveDialog} disabled={isRemovingDiscount} sx={{ bgcolor: alpha('#000', 0.04) }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          {selectedOrder && (
            <>
              <Alert severity="warning" sx={{ mb: 3, borderRadius: '8px' }}>
                You are about to remove the discount/complimentary status from order <strong>{selectedOrder.order_number}</strong>.
                This will restore the original total amount.
              </Alert>

              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: alpha('#F44336', 0.04),
                  borderRadius: '10px',
                  border: `1px solid ${alpha('#F44336', 0.1)}`,
                  mb: 3,
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    <strong>Current:</strong> {selectedOrder.is_complimentary ? 'Complimentary' : `${getDiscountTypeLabel(selectedOrder.discount_type)} - ${getDiscountDisplayText(selectedOrder)}`}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    <strong>Current Total:</strong> {formatCurrency(selectedOrder.total_amount)}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    <strong>Original Total:</strong> {formatCurrency(selectedOrder.original_total_amount || selectedOrder.subtotal + (selectedOrder.tax || 0))}
                  </Typography>
                  {selectedOrder.table_name && (
                    <Typography variant="body2" fontWeight={600}>
                      <strong>Table:</strong> {selectedOrder.table_name}
                    </Typography>
                  )}
                </Box>
              </Paper>
            </>
          )}

          <TextField
            fullWidth
            label="Reason for Removal (Optional)"
            placeholder="Why are you removing the discount?"
            value={removeReason}
            onChange={(e) => setRemoveReason(e.target.value)}
            multiline
            rows={2}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Additional Notes (Optional)"
            placeholder="Any additional notes..."
            value={removeNotes}
            onChange={(e) => setRemoveNotes(e.target.value)}
            multiline
            rows={2}
          />
        </DialogContent>

        <DialogActions
          sx={{
            borderTop: `1px solid ${alpha('#000', 0.06)}`,
            pt: 2,
            px: 3,
            pb: 3,
            gap: 1.5,
            bgcolor: '#f8f9fa',
            borderRadius: '0 0 20px 20px',
          }}
        >
          <Button
            onClick={handleCloseRemoveDialog}
            disabled={isRemovingDiscount}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px', px: 4, py: 1 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRemoveDiscount}
            variant="contained"
            color="error"
            disabled={isRemovingDiscount}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '10px',
              px: 5,
              py: 1.2,
              fontSize: '1rem',
            }}
            startIcon={isRemovingDiscount ? <CircularProgress size={22} color="inherit" /> : <DeleteIcon />}
          >
            {isRemovingDiscount ? 'Removing...' : 'Remove Discount'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============================================ */}
      {/* CANCEL ORDER DIALOG */}
      {/* ============================================ */}
      <Dialog
        open={cancelDialogOpen}
        onClose={handleCloseCancelDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            maxHeight: '80vh',
          }
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: `1px solid ${alpha('#000', 0.06)}`,
            pb: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: '#f8f9fa',
            borderRadius: '20px 20px 0 0',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <DeleteIcon sx={{ color: '#F44336', fontSize: 28 }} />
            <Typography variant="h5" component="span" fontWeight={700}>
              Cancel Order
            </Typography>
          </Box>
          <IconButton onClick={handleCloseCancelDialog} disabled={isCancelling} sx={{ bgcolor: alpha('#000', 0.04) }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          {selectedOrder && (
            <>
              <Alert severity="error" sx={{ mb: 3, borderRadius: '8px' }}>
                You are about to cancel order <strong>{selectedOrder.order_number}</strong>.
                This action cannot be undone.
              </Alert>

              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: alpha('#F44336', 0.04),
                  borderRadius: '10px',
                  border: `1px solid ${alpha('#F44336', 0.1)}`,
                  mb: 3,
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    <strong>Status:</strong> {getStatusLabel(selectedOrder.order_status)}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    <strong>Total:</strong> {formatCurrency(selectedOrder.total_amount)}
                  </Typography>
                  {selectedOrder.table_name && (
                    <Typography variant="body2" fontWeight={600}>
                      <strong>Table:</strong> {selectedOrder.table_name}
                    </Typography>
                  )}
                </Box>
              </Paper>
            </>
          )}

          <TextField
            fullWidth
            label="Reason for Cancellation (Optional)"
            placeholder="Why are you cancelling this order?"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            multiline
            rows={3}
          />
        </DialogContent>

        <DialogActions
          sx={{
            borderTop: `1px solid ${alpha('#000', 0.06)}`,
            pt: 2,
            px: 3,
            pb: 3,
            gap: 1.5,
            bgcolor: '#f8f9fa',
            borderRadius: '0 0 20px 20px',
          }}
        >
          <Button
            onClick={handleCloseCancelDialog}
            disabled={isCancelling}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px', px: 4, py: 1 }}
          >
            Keep Order
          </Button>
          <Button
            onClick={handleCancelOrder}
            variant="contained"
            color="error"
            disabled={isCancelling}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '10px',
              px: 5,
              py: 1.2,
              fontSize: '1rem',
            }}
            startIcon={isCancelling ? <CircularProgress size={22} color="inherit" /> : <DeleteIcon />}
          >
            {isCancelling ? 'Cancelling...' : 'Cancel Order'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============================================ */}
      {/* AUDIT HISTORY DIALOG */}
      {/* ============================================ */}
      <Dialog
        open={auditDialogOpen}
        onClose={handleCloseAuditDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            maxHeight: '90vh',
          }
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: `1px solid ${alpha('#000', 0.06)}`,
            pb: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: '#f8f9fa',
            borderRadius: '20px 20px 0 0',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <HistoryIcon sx={{ color: '#1976D2', fontSize: 28 }} />
            <Typography variant="h5" component="span" fontWeight={700}>
              Discount Audit Trail
            </Typography>
            {selectedOrder && (
              <Chip label={selectedOrder.order_number} size="small" sx={{ fontWeight: 700 }} />
            )}
          </Box>
          <IconButton onClick={handleCloseAuditDialog} sx={{ bgcolor: alpha('#000', 0.04) }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 2, overflowY: 'auto' }}>
          {isLoadingAudit ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <AuditTrail audits={auditHistory} theme={theme} />
          )}
        </DialogContent>

        <DialogActions
          sx={{
            borderTop: `1px solid ${alpha('#000', 0.06)}`,
            pt: 2,
            px: 3,
            pb: 3,
            gap: 1,
            bgcolor: '#f8f9fa',
            borderRadius: '0 0 20px 20px',
          }}
        >
          <Button
            onClick={handleCloseAuditDialog}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px', px: 4, py: 1 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============================================ */}
      {/* SNACKBAR */}
      {/* ============================================ */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        TransitionComponent={Fade}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            width: '100%',
            borderRadius: '10px',
            fontWeight: 600,
            '& .MuiAlert-message': { fontWeight: 600 },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Floating Action Button for Mobile */}
      <Fab
        color="primary"
        aria-label="refresh"
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          display: { xs: 'flex', md: 'none' },
          zIndex: 1000,
          bgcolor: locationColor,
          '&:hover': { bgcolor: alpha(locationColor, 0.8) },
        }}
        onClick={handleRefresh}
        disabled={isRefreshing}
      >
        {isRefreshing ? <CircularProgress size={24} color="inherit" /> : <RefreshIcon />}
      </Fab>
    </Suspense>
  );
};

export default OrderDiscountManager;